import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClassificationWorkflow } from '@/features/reports/classification/classificationWorkflow.types';
import { useSchoolBandsQuery } from './useSchoolBandsQuery';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';

/**
 * Year selection + GET /api/app/school-bands + wizard state.
 * - Bands from API → show wizard (edit), hydrated from first item.
 * - No bands → empty state; "Create workflow" is session-only per year (reset when year changes).
 */
export function useManageClassification() {
  const defaultYear = useDefaultYear();
  const [selectedYear, setSelectedYear] = useState('');
  const bandsQuery = useSchoolBandsQuery(selectedYear || undefined);

  useEffect(() => {
    if (!selectedYear && defaultYear) setSelectedYear(defaultYear);
  }, [defaultYear]);

  /** Session-only: cleared whenever `selectedYear` changes. */
  const [showNewWorkflowWizard, setShowNewWorkflowWizard] = useState(false);
  const [workflow, setWorkflow] = useState<ClassificationWorkflow | null>(null);

  /**
   * After the user clears the year (e.g. post-submit), the next hydration for any year must
   * rebuild from GET data — otherwise `prev.year === selectedYear` would resurrect stale
   * stepIndex / drafts when re-selecting the same year.
   */
  const discardWorkflowHydrationRef = useRef(false);

  const hasBandsFromApi = (bandsQuery.data?.totalCount ?? 0) > 0;

  /** Changing year forgets "Create workflow" and clears local wizard state. */
  useEffect(() => {
    setShowNewWorkflowWizard(false);
    setWorkflow(null);
    if (!selectedYear.trim()) {
      discardWorkflowHydrationRef.current = true;
    }
  }, [selectedYear]);

  /** Hydrate wizard from API when bands exist for the selected year (once per year load; refetch won't clobber edits). */
  useEffect(() => {
    if (!selectedYear.trim()) return;
    if (showNewWorkflowWizard) return;
    if (!bandsQuery.isSuccess || !bandsQuery.data) return;
    if (bandsQuery.data.totalCount <= 0 || !bandsQuery.data.items[0]) return;

    const b = bandsQuery.data.items[0];
    if (b.year !== Number(selectedYear)) return;

    setWorkflow((prev) => {
      const baseline: ClassificationWorkflow = {
        year: selectedYear,
        minSchoolsPerBand: b.minSchoolsPerBand,
        stepIndex: 0,
        updatedAt: b.lastModificationTime ?? new Date().toISOString(),
      };
      if (discardWorkflowHydrationRef.current) {
        discardWorkflowHydrationRef.current = false;
        return baseline;
      }
      if (prev && prev.year === selectedYear) return prev;
      return baseline;
    });
  }, [
    selectedYear,
    showNewWorkflowWizard,
    bandsQuery.isSuccess,
    bandsQuery.data,
  ]);

  const patchWorkflow = useCallback((patch: Partial<ClassificationWorkflow>) => {
    setWorkflow((prev) =>
      prev ? { ...prev, ...patch, updatedAt: new Date().toISOString() } : null
    );
  }, []);

  const startNewWorkflow = useCallback(() => {
    if (!selectedYear.trim()) return;
    setShowNewWorkflowWizard(true);
    setWorkflow({
      year: selectedYear,
      minSchoolsPerBand: null,
      stepIndex: 0,
      updatedAt: new Date().toISOString(),
    });
  }, [selectedYear]);

  return {
    selectedYear,
    setSelectedYear,
    bandsQuery,
    workflow,
    patchWorkflow,
    hasBandsFromApi,
    showNewWorkflowWizard,
    startNewWorkflow,
  };
}
